import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { SessionFormModal } from '@/apps/coach/components/admin/SessionFormModal';
import { Button } from '@/components/ui/button';
import { ChevronLeft, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { DebriefRecorder } from '@/apps/coach/components/admin/DebriefRecorder';
import { DebriefFields } from '@/apps/coach/components/admin/DebriefFields';
import { PlanGenerator } from '@/apps/coach/components/admin/PlanGenerator';
import type { CoachSessionDebrief, CoachSessionWithDebrief, SessionFormat } from '@/types/coach';

const FORMAT_LABEL: Record<SessionFormat, string> = {
  in_person: 'Persönlich',
  phone: 'Telefon',
  video: 'Video-Call',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function SessionDetail() {
  const { clientId, sessionId } = useParams<{ clientId: string; sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<CoachSessionWithDebrief | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [plan, setPlan] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [debriefMode, setDebriefMode] = useState<'view' | 'recording' | 'manual'>('view');

  useEffect(() => {
    if (!sessionId) return;
    let abandoned = false;
    setDebriefMode('view');
    api.coach.sessions.get(sessionId)
      .then((s) => {
        if (abandoned) return;
        setSession(s);
        setNotes(s.notes ?? '');
        setPlan(s.plan ?? '');
      })
      .catch((err) => {
        if (!abandoned) setError(err instanceof Error ? err.message : 'Session nicht gefunden.');
      });
    return () => { abandoned = true; };
  }, [sessionId]);

  function handleDebriefReady(d: CoachSessionDebrief) {
    if (!session) return;
    setSession({ ...session, debrief: d });
    setDebriefMode('view');
    toast.success('Nachbereitung erstellt. Du kannst sie jetzt anpassen und speichern.');
  }

  function handleDebriefChanged(d: CoachSessionDebrief) {
    if (!session) return;
    setSession({ ...session, debrief: d });
  }

  async function handleDebriefReset() {
    if (!session?.debrief) return;
    try {
      await api.coach.sessions.deleteDebrief(session.id);
      setSession({ ...session, debrief: null });
      setDebriefMode('view');
      toast.success('Nachbereitung verworfen.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Verwerfen fehlgeschlagen.');
    }
  }

  async function startManual() {
    if (!session) return;
    try {
      const debrief = await api.coach.sessions.updateDebrief(session.id, {
        toolsUsed: '', mood: '', keyInsights: '', improvements: '', nextSteps: '',
      });
      setSession({ ...session, debrief });
      setDebriefMode('manual');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Konnte nicht starten.');
    }
  }

  async function saveNotes() {
    if (!session) return;
    setSavingNotes(true);
    try {
      const updated = await api.coach.sessions.update(session.id, {
        notes: notes.trim() ? notes.trim() : null,
      });
      setSession({ ...session, ...updated, debrief: session.debrief });
      toast.success('Notizen gespeichert.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSavingNotes(false);
    }
  }

  async function savePlan() {
    if (!session) return;
    setSavingPlan(true);
    try {
      const updated = await api.coach.sessions.update(session.id, {
        plan: plan.trim() ? plan.trim() : null,
      });
      setSession({ ...session, ...updated, debrief: session.debrief });
      toast.success('Fahrplan gespeichert.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSavingPlan(false);
    }
  }

  async function handleDelete() {
    if (!session) return;
    const ok = window.confirm('Session wirklich löschen? Die Nachbereitung wird ebenfalls entfernt.');
    if (!ok) return;
    try {
      await api.coach.sessions.delete(session.id);
      toast.success('Session gelöscht.');
      navigate(routes.coach.client(clientId!), { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (error) {
    return (
      <AdminShell>
        <div className="px-8 py-12 max-w-2xl mx-auto">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(routes.coach.client(clientId!))}>
            Zurück zum Klienten
          </Button>
        </div>
      </AdminShell>
    );
  }

  if (!session) {
    return (
      <AdminShell>
        <div className="px-8 py-12 text-sm text-muted-foreground">Lade…</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell pageTitle={`Session vom ${formatDate(session.date)}`}>
      <div className="px-4 md:px-8 py-6 max-w-3xl mx-auto w-full">
        <button
          type="button"
          onClick={() => navigate(routes.coach.client(clientId!))}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft size={14} /> Zurück zum Klienten
        </button>

        <div className="flex items-start justify-between mb-6 pb-4 border-b border-border">
          <div>
            <h1 className="text-xl font-semibold">Session vom {formatDate(session.date)}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {FORMAT_LABEL[session.format]}
              {session.durationMinutes !== null && ` · ${session.durationMinutes} min`}
            </p>
          </div>
          <div className="relative">
            <Button size="sm" variant="ghost" onClick={() => setMenuOpen((o) => !o)}>
              <MoreHorizontal size={16} />
            </Button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 rounded-md border border-border bg-popover shadow-md z-10">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setEditOpen(true); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2"
                >
                  <Pencil size={14} /> Session bearbeiten
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); handleDelete(); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2 text-destructive"
                >
                  <Trash2 size={14} /> Session löschen
                </button>
              </div>
            )}
          </div>
        </div>

        <section className="mb-8">
          <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Fahrplan</h3>
          {!session.plan && (
            <div className="mb-3">
              <PlanGenerator
                sessionId={session.id}
                onAccepted={async (suggestion) => {
                  setPlan(suggestion);
                  setSavingPlan(true);
                  try {
                    const updated = await api.coach.sessions.update(session.id, { plan: suggestion });
                    setSession({ ...session, ...updated, debrief: session.debrief });
                    toast.success('Fahrplan übernommen.');
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
                  } finally {
                    setSavingPlan(false);
                  }
                }}
              />
            </div>
          )}
          <textarea
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            placeholder="Notizen zur kommenden Sitzung…"
            className="w-full rounded-md border border-input bg-background p-3 text-sm min-h-[100px] resize-y"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={savePlan} disabled={savingPlan || (plan.trim() === (session.plan ?? ''))}>
              {savingPlan ? 'Speichere…' : 'Fahrplan speichern'}
            </Button>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Nachbereitung</h3>
          {session.debrief ? (
            <DebriefFields
              sessionId={session.id}
              debrief={session.debrief}
              onChanged={handleDebriefChanged}
              onResetRequested={handleDebriefReset}
            />
          ) : debriefMode === 'recording' ? (
            <DebriefRecorder
              sessionId={session.id}
              onDebriefReady={handleDebriefReady}
              onCancel={() => setDebriefMode('view')}
            />
          ) : (
            <div className="rounded-xl border border-border bg-card p-6 text-center space-y-3">
              <Button
                size="lg"
                onClick={() => setDebriefMode('recording')}
                className="gap-2"
              >
                🎙️ Mit KI nachbereiten
              </Button>
              <div className="text-xs text-muted-foreground">
                oder{' '}
                <button
                  type="button"
                  onClick={startManual}
                  className="underline hover:text-foreground"
                >
                  manuell ausfüllen
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="mb-8">
          <h3 className="text-xs font-medium uppercase text-muted-foreground mb-2">Notizen (manuell)</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Eigene Notizen zur Session…"
            className="w-full rounded-md border border-input bg-background p-3 text-sm min-h-[100px] resize-y"
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={saveNotes}
              disabled={savingNotes || (notes.trim() === (session.notes ?? ''))}
            >
              {savingNotes ? 'Speichere…' : 'Notizen speichern'}
            </Button>
          </div>
        </section>
      </div>

      <SessionFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        clientId={clientId!}
        initial={session}
        onSaved={(saved) => setSession({ ...session, ...saved, debrief: session.debrief })}
      />
    </AdminShell>
  );
}
